import { t } from '@transifex/native';

import { show } from 'pkg/actions/flashes';
import type { CreateUserPayload } from 'pkg/actions/users/interface';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import type { Capabilities, RoleType } from 'pkg/api/models/membership';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

import { Event, trackEvent } from 'components/analytics';

export interface InheritedMembership {
	userId: number;
	role: RoleType;
	capabilities: Capabilities[];
}

export const archiveUsers = async (
	organizationId: number,
	userIds: number[]
): Promise<void> => {
	const request = await sdk.destroy(
		endpoints.Users.Delete(organizationId),
		{},
		{ ids: userIds }
	);

	if (!request.ok) {
		show({
			title: t(`Failed to archive user`),
			message: t(`Contacts could not be archived.`),
		});
	} else {
		show({
			title: t(`Archived user`),
			message: t(`Contact(s) were succesfully archived.`),
		});
	}
};

export const restoreUser = async (
	organizationId: number,
	userId: number
): Promise<void> => {
	const request = await sdk.post(
		endpoints.Users.Undelete(organizationId, userId)
	);
	const response = await request.json();

	if (!request.ok) {
		show({
			title: crash().title,
			message: response.error,
		});
	} else {
		show({
			title: t(`Restoration complete`),
			message: t(`Contact were succesfully restored.`),
		});
	}
};

export const update = async (
	organizationId: number,
	userId: number,
	data: Record<string, any>
): Promise<boolean> => {
	const request = await sdk.patch(
		endpoints.Users.Update(organizationId, userId),
		{},
		data
	);

	if (!request.ok) {
		if (request.status === 409) {
			const msg = await request.json();
			if (msg.error === 'duplicate lok swedish personal id') {
				show(
					{
						title: t('The Personal ID already exists in this organization'),
						message: t(
							'Contact an organization administrator to resolve this, then return to complete this form.'
						),
					},
					request.status
				);
				return false;
			}
		}

		show(
			{
				title: t('An error occured'),
			},
			request.status
		);

		return false;
	}

	show(
		{
			title: t(`Successfully saved!`),
		},
		request.status
	);

	return true;
};

export async function requestPasswordChange(email: string): Promise<boolean> {
	const response = await sdk.post(
		endpoints.AccountTokens.CreatePasswordResetToken(),
		{},
		{ email: email }
	);

	if (!response.ok) {
		switch (response.status) {
			case 400:
				show({
					title: t('An error occured'),
				});
				break;
			case 500:
				show({
					title: t('Something went wrong'),
				});
				break;
			case 404:
				show({
					title: t(`{email} not found.`, { email }),
				});
				break;
		}

		return false;
	}

	show(
		{
			title: t(`Reset link sent`),
			message: t(`Sent to {email}`, { email }),
		},
		200
	);

	return true;
}

export const passwordChange = async (
	password: string,
	token: string,
	showSuccessFlash: boolean = true
): Promise<boolean> => {
	const response = await sdk.post(
		endpoints.Accounts.ResetPassword(),
		{},
		{
			password: password,
		},
		{
			'X-User-Token': token,
		}
	);

	const json = await response.json();

	if (!response.ok) {
		switch (response.status) {
			case 400: {
				if (json.error === 'password must be at least 16 characters') {
					show({
						title: t('Your password needs to be at least 16 characters long'),
					});
					break;
				} else {
					show({
						title: t('An error occured'),
					});
					break;
				}
			}
			case 403:
				show({
					title: t('Forbidden'),
				});
				break;
			case 500:
				show({
					title: t('Something went wrong'),
				});
				break;
			case 404:
				show({
					title: t(`Invalid reset code. It could be old or already used.`),
				});
				break;
			default:
		}

		return false;
	}

	if (showSuccessFlash) {
		show(
			{
				title: t(
					`Your password has been changed, you can now log in using your new password.`
				),
			},
			200
		);
	}

	return true;
};

export const createUser = async (
	payload: CreateUserPayload
): Promise<[Response, models.user.User]> => {
	const [request, response, error] = await create<
		CreateUserPayload,
		models.user.User
	>(endpoints.Users.Create(payload.organizationId), payload);

	if (!request.ok) {
		if (request.status === 409) {
			if (error.error === 'duplicate lok swedish personal id') {
				show(
					{
						title: t('The Personal ID already exists in this organization'),
						message: t(
							'Contact an organization administrator to resolve this, then return to complete this form.'
						),
					},
					request.status
				);
			}
		} else {
			show({
				title: error.error,
			});
		}

		return [request, null];
	}

	return [request, response];
};

export const mergeUsers = async (
	organizationId: number,
	userId: number,
	sourceUserId: number,
	data: { [key: string]: string | number }
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.Users.Merge(organizationId, userId, sourceUserId)
	);
	const resp = await request.json();

	if (!request.ok) {
		const flashMessage = {
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		};

		if (request.status === 400) {
			flashMessage.message = resp.error;
		}

		show(flashMessage);
		return false;
	}

	const patchRequest = await update(organizationId, userId, data);

	if (!patchRequest) {
		show({
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		});
		return false;
	}

	show({
		title: t(`Merge completed!`),
		message: t(`{name} has been updated.`, {
			name: data.firstName,
		}),
	});

	trackEvent(Event.UserMerge);
	return true;
};

export const fetchPaymentSource = async (
	organizationId: number,
	userId: number
): Promise<boolean> => {
	const request = await sdk.get(
		endpoints.Users.DefaultPaymentSource(organizationId, userId)
	);

	if (!request.ok) {
		return false;
	}

	return true;
};

export const setLegalGuardianAsBillingContact = async (
	organizationId: number,
	userIds: number[]
): Promise<[models.user.User[], Response]> => {
	const request = await sdk.post(
		endpoints.Users.SetBillingUserToLegalGuardian(organizationId),
		{},
		{ userIDs: userIds }
	);

	if (!request.ok) {
		show({
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		});

		return [null, request];
	}

	const response = await request.json();

	return [response, request];
};
